import { get } from 'es-toolkit/compat'
import type { Tel } from './tel-types.js'
import { prepareForLog } from './prepare-for-log/prepare-for-log.js'
import type { RemoteError, TelError, TelErrorJson } from './tel-error-data.js'
import { getRouteTel, nullTel } from './tel.js'

// instrument a service to automatically pass tel to each function call
export function withTelBoundary<T>(service: T, tel: Tel, label: string): T {
  function createProxy(path: (string | symbol)[], target: a): a {
    if (typeof target === 'function') {
      return (...args: a[]) => {
        const input = args[0] || {}
        const effectiveTel: Tel = input.tel || tel
        return effectiveTel.startSpan(
          { name: path.join('.'), target: '3rdParty', label },
          (inputArg: a, spanTel: Tel) => {
            const fn = get(service, path)
            return fn({ ...inputArg, tel: spanTel })
          },
          input,
        )
      }
    }

    // For nested objects, return a new proxy that accumulates the property path.
    if (target !== null && typeof target === 'object') {
      return new Proxy(target, {
        get(_target, prop) {
          const newPath = path.concat(prop)
          return createProxy(newPath, _target[prop])
        },
      })
    }

    // For other values (non functions, non objects), return them directly.
    return target
  }

  // Return the proxy wrapping the whole monite client.
  return createProxy([], service)
}

// instrument fetch to automatically log request and response and trace errors
export const makeThirdPartyFetchWithTel =
  (label: string, ErrorClass: new (data: RemoteError) => Error) =>
  (data: { tel?: Tel; extremeResponseMasking?: boolean }): ((
    req: Request,
  ) => Promise<Response>) => {
    return async (req) =>
      (data.tel || nullTel).startSpan(
        { name: `${req.method} ${req.url}`, target: '3rdParty', label },
        async (tel) => {
          if (req.headers.get('Content-Type') === 'application/json') {
            req
              .clone() // clone request before consuming it to avoid errors
              .json()
              .then((reqBody) => tel._updateSpan({ input: reqBody }))
              .catch(() => {}) // for some reason in safari we get `The string did not match the expected pattern` error which means json.parse failed due to a malformed json. Deep research indicates the danger of cloning requests in safari.
          }

          const res = await fetch(req)

          if (!res.ok) {
            const responseData: a = await res.json()
            throw new ErrorClass({
              code: res.status,
              responseData,
            })
          }

          // Asked before the clone: capture is fail-closed, so parsing a third
          // party's response and extreme-masking it is work for a payload the
          // span would drop on the way in.
          if (tel.span.capturesOutput() && res.headers.get('Content-Type') === 'application/json') {
            res
              .clone()
              .json()
              .then((resBody) =>
                tel._updateSpan({
                  output: data.extremeResponseMasking
                    ? prepareForLog(resBody, { maskMode: 'extreme' })
                    : resBody,
                }),
              )
          }

          return res
        },
      )
  }

export const makeClientFetchWithTel = (
  label: string,
  ErrorClass: new (data: RemoteError) => Error = DefaultRemoteError,
  fetchMethod: typeof fetch = fetch,
) => {
  return async (url: string, init?: RequestInit) => {
    const req = new Request(url, init)
    return getRouteTel().startSpan(
      { name: `${req.method} ${req.url}`, target: 'fetch', label },
      async (tel: Tel) => {
        if (req.headers.get('Content-Type') === 'application/json') {
          req
            .clone() // clone request before consuming it to avoid errors
            .json()
            .then((reqBody) => tel._updateSpan({ input: reqBody }))
            .catch(() => {}) // read above safari comment for error `The string did not match the expected pattern`
        }

        const res = await fetchMethod(req)

        if (!res.ok)
          throw new ErrorClass({
            code: res.status,
            responseData: (await res.json()) as a,
          })

        // Same as the third-party fetch above: no clone, no parse and no
        // extreme-mask for a payload the span's gate will not record.
        if (tel.span.capturesOutput() && res.headers.get('Content-Type') === 'application/json') {
          res
            .clone()
            .json()
            .then((resBody) =>
              tel._updateSpan({ output: prepareForLog(resBody, { maskMode: 'extreme' }) }),
            ) // makeClientFetchWithTel is used for 3rd party services that are controlled by other teams - mask everything
            .catch(() => {}) // read above safari comment for error `The string did not match the expected pattern`
        }

        return res
      },
    )
  }
}

class DefaultRemoteError extends Error implements TelError {
  code: number
  data: unknown
  serializable = true

  constructor(data: RemoteError) {
    super(`Request failed with status ${data.code}`)
    this.name = 'DefaultRemoteError'
    this.code = data.code
    this.data = data.responseData
  }

  toJSON(): TelErrorJson {
    return {
      type: 'default-remote',
      code: this.code,
      message: this.message,
      stack: this.stack,
      errorData: typeof this.data === 'string' ? this.data : JSON.stringify(this.data),
    }
  }
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type a = any
